/**
 * Talent tree (GDD §2 — three paths, 15 points each, 1 point per level-up).
 *
 * The GDD gives each five-point band a range (e.g. "+3 % bis +15 %"), i.e. a
 * linear per-point gain across the band. `talentEffects` turns invested points
 * into the concrete modifiers the systems consume.
 */

import type { TalentPathKey } from '../types.js';

export interface TalentTierDef {
  from: number;
  to: number;
  effect: string;
  /** Gain per point inside this band. */
  perPoint: number;
}

export interface TalentPathDef {
  key: TalentPathKey;
  label: string;
  tiers: [TalentTierDef, TalentTierDef, TalentTierDef];
}

export const TALENT_PATHS: Record<TalentPathKey, TalentPathDef> = {
  economy: {
    key: 'economy',
    label: 'Wirtschaft & Handel',
    tiers: [
      { from: 1, to: 5, effect: '+3 % bis +15 % Förderleistung aller Primärminen.', perPoint: 0.03 },
      { from: 6, to: 10, effect: '−1 % bis −5 % Steuern auf Transaktionen im Händler-Portal.', perPoint: 0.01 },
      { from: 11, to: 15, effect: '+5 % bis +25 % Ladekapazität aller Transportschiffe.', perPoint: 0.05 },
    ],
  },
  war: {
    key: 'war',
    label: 'Krieg & Expansion',
    tiers: [
      { from: 1, to: 5, effect: '−2 % bis −10 % Treibstoffverbrauch (Isotop-3) für Flottenmanöver.', perPoint: 0.02 },
      { from: 6, to: 10, effect: '+3 % bis +15 % Waffenschaden für Großkampfschiffe.', perPoint: 0.03 },
      { from: 11, to: 15, effect: 'Warp-Booster wirken um 10 % bis 30 % effizienter.', perPoint: 0.02 },
    ],
  },
  astronomy: {
    key: 'astronomy',
    label: 'Astronomie & Defensiv-Infrastruktur',
    tiers: [
      { from: 1, to: 5, effect: '+5 % bis +25 % Chance auf seltene Kometen-Spawns im Heimatsystem.', perPoint: 0.05 },
      { from: 6, to: 10, effect: 'Frühwarnsystem schlägt 2 bis 10 Minuten früher an.', perPoint: 2 },
      { from: 11, to: 15, effect: '+10 % bis +50 % Reparaturquote für zerstörte Defensiv-Anlagen.', perPoint: 0.1 },
    ],
  },
};

export const TALENT_MAX_PER_PATH = 15;

/** Points inside a band: invested 7 in economy → band1 = 5, band2 = 2, band3 = 0. */
function bandPoints(invested: number, tier: 0 | 1 | 2): number {
  const start = tier * 5;
  return Math.max(0, Math.min(5, invested - start));
}

export interface TalentEffects {
  /** Multiplier on primary mine output (1.0 = neutral). */
  mineBonus: number;
  /** Absolute tax reduction (subtract from merchant tax). */
  taxReduction: number;
  /** Multiplier on transporter cargo. */
  cargoBonus: number;
  /** Fuel use multiplier for fleet manoeuvres. */
  fuelFactor: number;
  /** Extra damage fraction for capital ships (cruiser and up). */
  capitalDamageBonus: number;
  /** Early-warning lead time in minutes. */
  earlyWarningMinutes: number;
  /** Fraction of destroyed defence repaired after battles. */
  defenseRepairRate: number;
  /** Additional rare-comet chance. */
  cometChanceBonus: number;
}

export function talentEffects(invested: Record<TalentPathKey, number>): TalentEffects {
  const eco = invested.economy ?? 0;
  const war = invested.war ?? 0;
  const astro = invested.astronomy ?? 0;
  return {
    mineBonus: 1 + bandPoints(eco, 0) * 0.03,
    taxReduction: bandPoints(eco, 1) * 0.01,
    cargoBonus: 1 + bandPoints(eco, 2) * 0.05,
    fuelFactor: 1 - bandPoints(war, 0) * 0.02,
    capitalDamageBonus: bandPoints(war, 1) * 0.03,
    earlyWarningMinutes: bandPoints(astro, 1) * 2,
    defenseRepairRate: bandPoints(astro, 2) * 0.1,
    cometChanceBonus: bandPoints(astro, 0) * 0.05,
  };
}
